import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import avatecLogo from '../../assets/logos/avatec-white.png';
import './Slide09Gallery.css';

const galleryItems = [1, 2, 7, 8, 9, 10, 11, 12].map((n) => {
  const number = String(n).padStart(2, '0');
  return {
    id: `gallery-${number}`,
    src: `/images/gallery/gallery-${number}.jpg`,
    alt: `Gallery image ${number}`,
    tag: number,
  };
});

function Slide09Gallery() {
  const prefersReduced = useReducedMotion();
  const reduceMotion = !!prefersReduced;
  const [isVisible, setIsVisible] = useState(false);
  const [activeItem, setActiveItem] = useState<null | (typeof galleryItems)[number]>(null);
  const ease: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (!activeItem) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveItem(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeItem]);

  const fadeUp = (delay = 0) =>
    reduceMotion
      ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
      : {
          initial: { opacity: 0, y: 18 },
          animate: isVisible ? { opacity: 1, y: 0 } : {},
          transition: { duration: 0.7, delay, ease },
        };

  return (
    <div className="slide slide-gallery">
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" />

      <div className="gallery-bg">
        <div className="gallery-grid-bg" />
        <div className="gallery-glow gallery-glow-left" />
        <div className="gallery-glow gallery-glow-right" />
        <div className="gallery-noise" />
      </div>

      <div className="gallery-content">
        <motion.div className="gallery-header" {...fadeUp(0.05)}>
          <span className="gallery-eyebrow">AVATEC MARINE</span>
          <h1 className="gallery-title">PHOTO GALLERY</h1>

        </motion.div>

        <div className="gallery-grid">
          {galleryItems.map((item, index) => (
            <motion.button
              key={item.id}
              className={`gallery-item gallery-item--${index + 1}`}
              type="button"
              onClick={() => setActiveItem(item)}
              aria-label={`Open gallery image ${item.tag}`}
              {...fadeUp(0.12 + index * 0.04)}
            >
              <img src={item.src} alt={item.alt} loading="lazy" />
              <span className="gallery-index">{item.tag}</span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeItem && (
          <motion.div
            className="gallery-modal"
            role="dialog"
            aria-modal="true"
            aria-label={activeItem.alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.25 }}
            onClick={() => setActiveItem(null)}
          >
            <motion.div
              className="gallery-modal-card"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: reduceMotion ? 0 : 0.25 }}
              onClick={(event) => event.stopPropagation()}
            >
              <img src={activeItem.src} alt={activeItem.alt} />
              <button
                type="button"
                className="gallery-modal-close"
                onClick={() => setActiveItem(null)}
                aria-label="Close image"
              >
                ×
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Slide09Gallery;
